import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, UserPlus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { fmtDate, useLabels } from "../lib/constants";
import { CompanyBadge, RoleBadge } from "../components/Badges";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const MATRIX = [
  ["cap1", true, true, true, true],
  ["cap2", true, true, true, true],
  ["cap3", false, true, true, true],
  ["cap4", false, true, true, true],
  ["cap5", false, true, true, true],
  ["cap6", false, true, true, true],
  ["cap7", false, true, true, true],
  ["cap8", false, true, true, true],
  ["cap9", false, false, true, true],
  ["cap10", false, false, false, true],
];

export default function Users() {
  const { user: me } = useAuth();
  const { t } = useLanguage();
  const L = useLabels();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "requester", company: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(apiError(e, t("users.loadFail")));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roleChoices = me.role === "super_admin"
    ? ["super_admin", "admin", "lawyer", "requester"]
    : ["lawyer", "requester"];

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
        company: form.company || null,
      });
      toast.success(t("users.created", { name: form.name }));
      setAddOpen(false);
      setForm({ name: "", email: "", password: "", role: "requester", company: "" });
      load();
    } catch (e2) {
      toast.error(apiError(e2, t("users.createFail")));
    } finally {
      setBusy(false);
    }
  };

  const updateUser = async (id, patch, successMsg) => {
    try {
      await api.patch(`/users/${id}`, patch);
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(apiError(e, t("users.updateFail")));
    }
  };

  const canEditRow = (u) => {
    if (u.id === me.id) return false;
    if (me.role === "super_admin") return true;
    return !["admin", "super_admin"].includes(u.role);
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("users.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {t(me.role === "super_admin" ? "users.subSuper" : "users.subAdmin")}
          </p>
        </div>
        <Button data-testid="user-management-add-user" onClick={() => setAddOpen(true)} className="bg-delta-navy hover:bg-delta-navyDark">
          <UserPlus className="me-2 h-4 w-4" /> {t("users.add")}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-white" data-testid="users-table">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-delta-navy border-t-transparent" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-start text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3 font-semibold">{t("users.colName")}</th>
                <th className="px-4 py-3 font-semibold">{t("users.colEmail")}</th>
                <th className="px-4 py-3 font-semibold">{t("users.colRole")}</th>
                <th className="px-4 py-3 font-semibold">{t("users.colCompany")}</th>
                <th className="px-4 py-3 font-semibold">{t("users.colStatus")}</th>
                <th className="px-4 py-3 font-semibold">{t("users.colJoined")}</th>
                <th className="px-4 py-3 font-semibold">{t("users.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="px-5 py-3 font-medium text-slate-800">{u.name}{u.id === me.id && <span className="ms-1 text-xs text-slate-400">{t("users.you")}</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    {canEditRow(u) && (me.role === "super_admin" || !["admin", "super_admin"].includes(u.role)) ? (
                      <Select
                        value={u.role}
                        onValueChange={(v) => updateUser(u.id, { role: v }, t("users.roleChanged", { name: u.name, role: L.role[v] }))}
                      >
                        <SelectTrigger data-testid={`role-select-${u.email}`} className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {roleChoices.map((r) => <SelectItem key={r} value={r}>{L.role[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <RoleBadge role={u.role} />
                    )}
                  </td>
                  <td className="px-4 py-3">{u.company ? <CompanyBadge company={u.company} /> : <span className="text-xs text-slate-400">—</span>}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" data-testid={`user-status-${u.email}`}
                      className={u.is_active !== false ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"}>
                      {u.is_active !== false ? t("users.active") : t("users.suspended")}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-400">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    {canEditRow(u) && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`toggle-user-${u.email}`}
                        onClick={() => updateUser(u.id, { is_active: u.is_active === false }, t("users.toggled", { name: u.name, state: t(u.is_active === false ? "users.stateReactivated" : "users.stateSuspended") }))}
                        className={u.is_active !== false ? "border-red-200 text-red-600 hover:bg-red-50" : "border-green-200 text-green-700 hover:bg-green-50"}
                      >
                        {u.is_active !== false ? t("users.suspend") : t("users.reactivate")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Card data-testid="permissions-matrix-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <ShieldCheck className="h-4 w-4 text-delta-lime" /> {t("users.matrixTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="permissions-matrix-table">
            <thead>
              <tr className="border-b bg-slate-50 text-start text-xs uppercase tracking-wide text-slate-500">
                <th className="px-6 py-2.5 font-semibold">{t("users.capability")}</th>
                <th className="px-4 py-2.5 text-center font-semibold">{L.role.requester}</th>
                <th className="px-4 py-2.5 text-center font-semibold">{L.role.lawyer}</th>
                <th className="px-4 py-2.5 text-center font-semibold">{L.role.admin}</th>
                <th className="px-4 py-2.5 text-center font-semibold">{L.role.super_admin}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {MATRIX.map((row) => (
                <tr key={row[0]}>
                  <td className="px-6 py-2.5 text-slate-700">{t(`users.${row[0]}`)}</td>
                  {row.slice(1).map((allowed, i) => (
                    <td key={`${row[0]}-${i}`} className="px-4 py-2.5 text-center">
                      <span className={`inline-block h-2.5 w-2.5 rounded-full ${allowed ? "bg-delta-lime" : "bg-slate-200"}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("users.addTitle")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label>{t("users.fullName")}</Label>
              <Input required minLength={2} data-testid="add-user-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("users.namePh")} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("login.email")}</Label>
              <Input required type="email" data-testid="add-user-email-input" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("users.emailPh")} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("users.tempPw")}</Label>
              <Input required minLength={8} data-testid="add-user-password-input" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t("users.pwPh")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("users.role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleChoices.map((r) => <SelectItem key={r} value={r}>{L.role[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("users.companyOptional")}</Label>
                <Select value={form.company || "none"} onValueChange={(v) => setForm({ ...form, company: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="add-user-company-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("common.none")}</SelectItem>
                    {Object.entries(L.company).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" data-testid="add-user-cancel-button" onClick={() => setAddOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" disabled={busy} data-testid="add-user-submit-button" className="bg-delta-navy hover:bg-delta-navyDark">
                {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {t("users.create")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
